import { useEffect, useState } from "react";
import type { RequiredLocaleCode } from "../../../packages/ui/src/localization.js";
import { apiRequest, qualificationAccountFromLocation } from "../../../packages/ui/src/runtime-client.js";
import { AccountCommerceHub, type AccountCommerceHubProps } from "./commerce/account-commerce.js";

const qualificationAccount = qualificationAccountFromLocation();

function verificationLabel(target: "KYC" | "TAX" | "FRAUD_REVIEW"): string {
  if (target === "KYC") return "Identity";
  if (target === "TAX") return "Tax";
  return "Account review";
}

function isAccountCommerceSummary(value: unknown): value is AccountCommerceHubProps {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<AccountCommerceHubProps>;
  return candidate.referral !== undefined && candidate.earnings !== undefined && candidate.points !== undefined &&
    candidate.savings !== undefined && candidate.wallet !== undefined && candidate.verification !== undefined &&
    candidate.activity !== undefined;
}

export function CommerceAccountSurface(props: { readonly signedIn: boolean; readonly locale?: RequiredLocaleCode }) {
  const [summary, setSummary] = useState<AccountCommerceHubProps | null>(null);
  const [message, setMessage] = useState("Loading rewards and payout details...");

  async function load() {
    if (!props.signedIn) return;
    try {
      const result = await apiRequest<AccountCommerceHubProps & { readonly activityTaxonomy?: readonly string[] }>("/v1/commerce/account/summary", { qualificationAccount });
      if (!isAccountCommerceSummary(result)) throw new Error("Rewards and payout details are temporarily unavailable.");
      setSummary(result);
      setMessage("Rewards, referrals, payouts, and verification details are up to date.");
    } catch (error) {
      setSummary(null);
      setMessage(error instanceof Error ? error.message : "Commerce state could not be loaded");
    }
  }

  useEffect(() => { void load(); }, [props.signedIn]);

  if (!props.signedIn) return null;
  return <section className="section" aria-labelledby="commerce-surface-title">
    <div className="section-heading"><div><span className="eyebrow">COMMERCE</span><h2 id="commerce-surface-title">Rewards, referrals, and payouts</h2></div></div>
    <p className="notice" aria-live="polite">{message}</p>
    {summary === null ? null : <AccountCommerceHub
      {...summary}
      {...(props.locale === undefined ? {} : { locale: props.locale })}
      callbacks={{
        onCopyReferralLink: (url) => {
          void navigator.clipboard?.writeText(url).then(() => setMessage("Referral link copied."), () => setMessage(url));
        },
        onShareReferralLink: (url) => {
          if ("share" in navigator && typeof navigator.share === "function") void navigator.share({ title: "Portability", url }).catch(() => setMessage(url));
          else void navigator.clipboard?.writeText(url).then(() => setMessage("Referral link copied."), () => setMessage(url));
        },
        onExportEarnings: (format) => {
          void apiRequest<{ readonly format: string; readonly mimeType: string }>("/v1/commerce/account/earnings/export?format=" + encodeURIComponent(format), { qualificationAccount })
            .then((exported) => setMessage(`${exported.format} earnings export prepared as ${exported.mimeType}.`))
            .catch((error: unknown) => setMessage(error instanceof Error ? error.message : "Earnings export could not be prepared"));
        },
        onRedeemPoints: () => setMessage("Points can be used only during an eligible checkout. Nothing is credited automatically."),
        onRecordSavingsEvidence: () => setMessage("Savings evidence comes from eligible bookings or approved price-review decisions."),
        onSubmitPriceMatch: (input) => {
          void apiRequest<{ readonly result: { readonly requestId: string; readonly status: string } }>("/v1/features/commerce/price_match/submit", {
            method: "POST",
            qualificationAccount,
            body: { ...input, currency: summary.savings.currency }
          }).then(async ({ result }) => {
            setMessage(`Price-match request ${result.requestId} is ${result.status.toLowerCase().replaceAll("_", " ")}.`);
            await load();
          }).catch((error: unknown) => setMessage(error instanceof Error ? error.message : "Price-match evidence could not be submitted"));
        },
        onEditWalletPreference: () => setMessage("Payout preferences update after identity, tax, and payout checks are complete."),
        onStartVerification: (target) => setMessage(`${verificationLabel(target)} verification is required before eligible held earnings can be released.`),
        onOpenActivity: () => setMessage("Activity details are saved according to the account retention policy.")
      }}
    />}
  </section>;
}
